import { PaymentType } from '@/types';
import type { Order } from '@/types';

/**
 * Item 4 — edit-order shipping-rule flow.
 *
 * Decides whether an order EDIT touched a field that can change the
 * shipping-rule outcome. The edit page uses this to avoid re-running rule
 * evaluation (and bouncing the merchant through the carrier step) for
 * purely cosmetic edits — phone, description, custom_id.
 *
 * The carrier-relevant fields mirror the inputs the backend rule evaluator
 * consumes (OrderRuleAssignmentService::buildEvaluatorInput) and the
 * payload built by `buildEvaluateShippingRulesRequest`:
 *
 *   - size            package size
 *   - is_fragile      fragile flag
 *   - type            order type (DEFAULT / RETURN / EXCHANGE)
 *   - destination     contact.region_uid
 *   - pickup address  saved_pick_up_address_uid / pick_up_address_uid
 *   - cod_amount      products subtotal + payment_type (PREPAID drops COD)
 *
 * A missing original order (e.g. first evaluation) is treated as a relevant
 * change so the rule flow always runs at least once.
 */
export function hasCarrierRelevantOrderChange(
  original: Order | null | undefined,
  next: Order,
): boolean {
  if (!original) return true;

  if ((original.size ?? undefined) !== (next.size ?? undefined)) return true;
  if (!!original.is_fragile !== !!next.is_fragile) return true;
  if (!!original.temperature_control !== !!next.temperature_control) return true;
  if ((original.type ?? 'DEFAULT') !== (next.type ?? 'DEFAULT')) return true;
  if ((original.contact?.region_uid ?? undefined) !== (next.contact?.region_uid ?? undefined)) {
    return true;
  }
  if (resolvePickupUid(original) !== resolvePickupUid(next)) return true;
  if (codAmount(original) !== codAmount(next)) return true;

  return false;
}

/** Pickup-address identity — `saved_pick_up_address_uid` falls back to `pick_up_address_uid`. */
function resolvePickupUid(order: Order): string | undefined {
  return order.saved_pick_up_address_uid ?? order.pick_up_address_uid ?? undefined;
}

/**
 * cod_amount equivalent — products subtotal for COD orders, 0 for PREPAID.
 * Mirrors `buildEvaluateShippingRulesRequest` so a payment-type flip or a
 * product-value change both register as carrier-relevant.
 */
function codAmount(order: Order): number {
  if (order.payment_type === PaymentType.PREPAID) return 0;
  const items = order.products ?? [];
  let total = 0;
  for (const p of items) {
    const price = Number((p as { price?: unknown })?.price ?? 0) || 0;
    const qtyRaw = (p as { quantity?: unknown })?.quantity;
    const qty = qtyRaw === undefined || qtyRaw === null || qtyRaw === '' ? 1 : Number(qtyRaw) || 0;
    total += price * qty;
  }
  return total;
}
